"use client";

// Ported verbatim from pf-platform hooks/use-phone-keyboard-box.ts @553406bc8 (LANE-mobile-keyboard-pattern-everywhere R8, 2026-10-29). Keep the two copies identical.

/**
 * usePhoneKeyboardBox — the locked phone keyboard pattern (Lars, 2026-10-29:
 * "lock this in … it should be fixed everywhere on mobile like that for input
 * fields") for any fixed pop-up with a text field. Same cure as the private
 * messages pop-up (messages-overlay-shell.tsx), in one place so every host
 * gets exactly the same thing:
 *
 *   - phones only (PHONE_SHEET_QUERY); desktop never sees the class or style;
 *   - keyboard up (useVisualViewportBox's steady keyboardOpen): the fixed host
 *     gets `kb-vv--on` and the visible box in --kb-vv-* variables, and
 *     globals.css makes it fill exactly the visible area, edge to edge; the
 *     card inside it (marked `data-kb-vv-card`) fills the host and its own
 *     body keeps native scroll;
 *   - while it fits the screen, drags nothing in it can scroll are cancelled
 *     (useLockViewportPan) so the page never pans under it;
 *   - keyboard down: nothing changes, today's card look.
 *
 * Usage: const kb = usePhoneKeyboardBox(hostRef);
 *        <div ref={hostRef} className={cx("x-overlay", kb.className)} style={kb.style}>
 */

import type { CSSProperties, RefObject } from "react";
import {
  PHONE_SHEET_QUERY,
  useLockViewportPan,
  useMediaQuery,
  useVisualViewportBox,
} from "@/hooks/use-visual-viewport-box";

export interface PhoneKeyboardBox {
  /** true while the keyboard is up on a phone and the host follows the visible area */
  on: boolean;
  /** "kb-vv--on" while on, otherwise "" */
  className: string;
  /** --kb-vv-* variables while on, otherwise undefined */
  style: CSSProperties | undefined;
}

export function usePhoneKeyboardBox(
  hostRef: RefObject<HTMLElement | null>,
  enabled = true,
): PhoneKeyboardBox {
  const isPhone = useMediaQuery(PHONE_SHEET_QUERY);
  const box = useVisualViewportBox(enabled && isPhone);
  const on = !!box?.keyboardOpen;
  useLockViewportPan(on, hostRef);
  const style =
    on && box
      ? ({
          "--kb-vv-left": `${box.left}px`,
          "--kb-vv-top": `${box.top}px`,
          "--kb-vv-width": `${box.width}px`,
          "--kb-vv-height": `${box.height}px`,
        } as CSSProperties)
      : undefined;
  return { on, className: on ? "kb-vv--on" : "", style };
}

/** Merge a host's own inline style with the keyboard box variables. */
export function withKbStyle(
  own: CSSProperties | undefined,
  kb: PhoneKeyboardBox,
): CSSProperties | undefined {
  if (!kb.style) return own;
  return own ? { ...own, ...kb.style } : kb.style;
}
